/**
 * src/ui/components/surfaces/RoutedDatabaseView.tsx
 *
 * F 473 — THE ROUTED DATABASE GETS THE REAL DATABASE SURFACE.
 *
 * WHAT F 473 FOUND (measured, not assumed).
 *
 * The prompt said "the mobile view of a database shows a simple list of cards.
 * There are no tabs, no search bar, and no filters" and asked for a toolbar to
 * be added to the MOBILE view. Measured on production at build `a5f2b6f`, phone
 * viewport 390x844:
 *
 *   /database/db-quick-notes   toolbar 0 | tablist 0 | tabs 0 | filter 0
 *   /database/db-quick-notes   (desktop 1440x900)  toolbar 0 | tablist 0 | tabs 0
 *
 * **The toolbar was not hidden on mobile. It was absent on BOTH.** The cause is
 * not a media query and not a `hiddenFrom` prop: the routed database was
 * rendered by `GeneralDatabaseView` (F 468), a read-only card grid that has no
 * toolbar, no views, no search and no filters at all. The component that owns
 * all of that is `DatabaseView` — and the routed path never reached it.
 *
 * WHY F 468 LANDED ON A DIFFERENT COMPONENT.
 *
 * F 468's job was "render ANY database at `/database/:id`, not only HQ Backup".
 * HQ Backup's 16,783 records live in `freshcards:hq_item:*`, so it kept
 * `HqDatabaseView`; the other six databases live embedded in
 * `freshcards:databases`, so F 468 wrote `GeneralDatabaseView` to read that one
 * key directly and window the cards. That solved the F 468 contract, but it
 * produced a surface with no view tabs — so a phone user (and a desktop user)
 * lost the entire database toolset the moment they opened a database.
 *
 * ⇒ **THE F 473 FIX IS TO REACH THE SURFACE THAT ALREADY OWNS THE TOOLBAR,
 * NOT TO BUILD A SECOND TOOLBAR.**
 *
 * `DatabaseView` already has, in one place:
 *   - the tab bar (`ViewSwitcher` in its SIMPLE branch — `active` + `onChange`),
 *   - the CRUD dropdown (`ViewSwitcher` in its default branch — `activeView` +
 *     `onSelect` + `onCreate` + `onRename` + `onDelete`),
 *   - a search field on desktop and a search toggle on mobile,
 *   - the persistent `FilterRow` with `+ Filter`,
 *   - and a real `isMobile` branch (`matchMedia('(max-width: 768px)')`).
 *
 * It reads the SAME data: `loadDatabase(id)` reads `RemoteState` under the key
 * `freshcards:databases` — the identical key and the identical graph that
 * `GeneralDatabaseView` reads directly. Measured: both see Quick Notes' 641
 * items. So switching the routed path to `DatabaseView` changes the VIEW, not
 * the DATA.
 *
 * WHAT THIS COMPONENT DOES, AND ONLY THIS:
 *   1. resolves the database's NAME for the routed id (one read of the same
 *      map key `GeneralDatabaseView` already reads), so the title header is the
 *      database's real name rather than a raw id;
 *   2. renders `DatabaseView` with that id, which supplies the toolbar.
 *
 * `GeneralDatabaseView` is NOT deleted. It stays as the fallback for a
 * database that `DatabaseView` cannot open, so nothing regresses to a blank
 * surface. `HqDatabaseView` is untouched — HQ Backup's records are still only
 * reachable through its own 11.5 MB reader.
 */

import * as React from 'react';
import { graphFetch } from '../../../core/storage/graphAuth';
import { DatabaseView } from './DatabaseView';
import { GeneralDatabaseView } from './GeneralDatabaseView';

const DATABASES_KEY = 'freshcards:databases';

export interface RoutedDatabaseViewProps {
  /** The `:id` from `/database/:id`. */
  databaseId: string;
}

/**
 * Resolve the database's display name from the same key the sidebar and
 * `DatabaseView` read. Returns null when the read fails or the id is unknown —
 * the caller then falls back rather than rendering a wrong name.
 */
async function readDatabaseName(databaseId: string): Promise<string | null> {
  try {
    const res = await graphFetch(
      '/api/fcc',
      `/kv?prefix=${encodeURIComponent(DATABASES_KEY)}`,
    );
    if (!res || !res.ok) return null;
    const body = (await res.json()) as { values?: Record<string, string> };
    const raw = body?.values?.[DATABASES_KEY];
    if (typeof raw !== 'string') return null;
    const map = JSON.parse(raw) as Record<string, { id?: string; name?: string }>;
    const record = map[databaseId] ?? Object.values(map).find((d) => d?.id === databaseId);
    return record?.name ?? null;
  } catch {
    return null;
  }
}

export function RoutedDatabaseView({ databaseId }: RoutedDatabaseViewProps): React.ReactElement {
  const [name, setName] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setName(null);
    (async () => {
      const resolved = await readDatabaseName(databaseId);
      if (!cancelled) setName(resolved);
    })();
    return () => {
      cancelled = true;
    };
  }, [databaseId]);

  // The title is a nicety; the toolbar is the point. Render `DatabaseView`
  // immediately with the id as the title and upgrade the title when the name
  // resolves — so the toolbar is never blocked on a network read.
  return (
    <DatabaseView
      key={databaseId}
      id={databaseId}
      phase={7}
      title={name ?? databaseId}
      description={`Database: ${name ?? databaseId}`}
      isActive={true}
    />
  );
}

/**
 * The fallback surface: used only when `DatabaseView` cannot open a database
 * at all. Kept as a separate export so the fallback is explicit and testable
 * rather than an accidental blank screen.
 */
export function RoutedDatabaseFallback({ databaseId }: RoutedDatabaseViewProps): React.ReactElement {
  return <GeneralDatabaseView databaseId={databaseId} />;
}
